import { ThemeProvider } from "@emotion/react";
import { CssBaseline } from "@mui/material";
import {
  createContext,
  lazy,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Route, Routes, useLocation } from "react-router";
import FrontendLoggerFactory from "utils/logging/FrontendLoggerFactory";

import { RoutePageLifecycle } from "./app/AppRouteLifecycle";
import { waitForFirebaseInit } from "./auth/helloCashflowDomain/firebase";
import AdminAccessDeniedModal from "./components/auth/AdminAccessDeniedModal";
import AdminAccessDeniedPanel from "./components/auth/AdminAccessDeniedPanel";
import { useAdminClaimCheck } from "./components/auth/hooks/useAdminClaimCheck";
import PermissionProtectedRoute from "./components/auth/PermissionProtectedRoute";
import { DevLogDownloadButton } from "./components/debug/DevLogDownloadButton";
import { EnvironmentSafetyBadges } from "./components/environment/EnvironmentSafetyBadges";
import { PreviewEmailOverrideGate } from "./components/previewEmail/PreviewEmailOverrideGate";
import { AdminStaticExportLauncherMount } from "./components/staticExport/AdminStaticExportLauncherMount";
import ApiHealthGate from "./components/ui/ApiHealthGate";
import Login from "./pages/Login";
import { createAdminTheme } from "./style/adminTheme";

import { installAdminCrossTabSignOutListener } from "@/auth/adminCrossTabSignOut";
import { useAuthState } from "@/auth/helloCashflowDomain/useAuthState";
import {
  buildLocalhostAdminLoginRequestUrl,
  resolveLocalhostAdminLoginReturnUrl,
  resolveLocalhostAutoLoginUserId,
  resolveProtectedRouteReturnUrl,
  shouldAttemptLocalhostAutoLogin,
  wasLocalhostTokenHandoffLoaded,
} from "@/auth/localAdminAutoLogin";
import { adminUiCopy } from "@/copy/adminUiCopy";
import { useAdminAuthSessionIsolation } from "@/hooks/useAdminAuthSessionIsolation";
import { useLocalAdminSessionRepair } from "@/hooks/useLocalAdminSessionRepair";
import { ADMIN_ROUTES } from "@/routing/adminRoutes";
import { ErrorBoundary } from "@/utils/errorHandling";
import { isAdminE2eBypassEnabled } from "@/utils/featureFlags";
import {
  isAdminScreenshotModeEnabled,
  shouldShowGlobalEnvironmentSafetyChrome,
  shouldShowLocalDevChrome,
} from "@/utils/localDevChromeVisibility";
import { emitAdminSessionBootSignal } from "@/utils/logging/adminSessionBootSignal";
import {
  CORRELATION_ID_HEADER,
  generateCorrelationId,
} from "@/utils/logging/correlationId";
import { sanitizeAdminRouteForLogging } from "@/utils/logging/sanitizeAdminRouteForLogging";
import { initializePerformanceMonitoring } from "@/utils/performance";
import { AdminVNextShell } from "@/vnext/app-shell/AdminVNextShell";
import { VNextCard, VNextPill } from "@/vnext/primitives";
import { ContactNextActionRouteCustody } from "@/vnext/routes/crm/contacts/record/ContactNextActionCustody";
import { GrowthFormClaimGate } from "@/vnext/routes/growth/forms/GrowthFormClaimGate";
import { GrowthFormDraftBoundary } from "@/vnext/routes/growth/forms/GrowthFormDraftBoundary";
import "@/vnext/theme/vnext.css";

const logger = FrontendLoggerFactory.getLogger().withComponent("App");
// Keep the admin claim requirement referentially stable so route/query rerenders do not
// accidentally retrigger claim-check effects that only care about the semantic "admin" requirement.
const ADMIN_REQUIRED_CLAIMS = ["admin"];
const SharedDocViewer = lazy(() => import("@/pages/public/SharedDocViewer"));
const RouteUnavailablePage = lazy(() => import("@/pages/RouteUnavailablePage"));

const AdminLoadingState = ({
  title,
  subtitle,
}: {
  title: string;
  subtitle: string;
}) => (
  <AdminVNextShell subtitle={subtitle} title={title}>
    <div className="vnext-blocking-shell">
      <div className="vnext-blocking-shell__stack">
        <div className="vnext-sidebar__badges">
          <VNextPill tone="accent">Admin</VNextPill>
          <VNextPill>Loading</VNextPill>
        </div>
        <VNextCard
          className="vnext-blocking-card"
          eyebrow="Admin navigation"
          subtitle={subtitle}
          title={title}
        >
          <div
            aria-live="polite"
            className="vnext-blocking-card__status"
            role="status"
          >
            <span aria-hidden="true" className="vnext-blocking-card__spinner" />
            {adminUiCopy.loading.adminWorkspaceStatus}
          </div>
        </VNextCard>
      </div>
    </div>
  </AdminVNextShell>
);

const LoadingFallback = () => (
  <AdminLoadingState
    subtitle={adminUiCopy.loading.adminRouteSubtitle}
    title={adminUiCopy.loading.adminRouteTitle}
  />
);

const PublicSharedDocLoadingFallback = () => (
  <div
    aria-live="polite"
    role="status"
    style={{
      alignItems: "center",
      display: "flex",
      justifyContent: "center",
      minHeight: "100vh",
    }}
  >
    Loading shared document...
  </div>
);

export const shouldBypassGlobalAdminHealthGate = (pathname: string) =>
  pathname === "/login" || pathname.startsWith("/share/docs/");

export const shouldBypassGlobalAdminAccessGate = (pathname: string) =>
  pathname.startsWith("/share/docs/");

export const shouldHoldForGlobalAdminClaimCheck = (args: {
  isAuthLoading: boolean;
  isCheckingClaims: boolean;
  pathname: string;
  userPresent: boolean;
}): boolean => {
  if (shouldBypassGlobalAdminAccessGate(args.pathname)) {
    return false;
  }

  if (!args.userPresent) {
    return false;
  }

  return args.isAuthLoading || args.isCheckingClaims;
};

export const shouldRenderGlobalAdminAccessDenied = (args: {
  claimCheckFailed: boolean;
  hasAdminClaim: boolean;
  pathname: string;
  userPresent: boolean;
}): boolean => {
  if (shouldBypassGlobalAdminAccessGate(args.pathname)) {
    return false;
  }

  if (!args.userPresent || args.claimCheckFailed) {
    return false;
  }

  return !args.hasAdminClaim;
};

interface AppAuthResolutionGateArgs {
  autoLoginInProgress: boolean;
  isAuthLoading: boolean;
  isFirebaseReady: boolean;
  pathname: string;
  shouldStartLocalhostAutoLogin: boolean;
}

export const shouldHoldForAppAuthResolution = ({
  autoLoginInProgress,
  isAuthLoading,
  isFirebaseReady,
  pathname,
  shouldStartLocalhostAutoLogin,
}: AppAuthResolutionGateArgs): boolean => {
  if (shouldBypassGlobalAdminAccessGate(pathname)) {
    return false;
  }

  return (
    !isFirebaseReady ||
    isAuthLoading ||
    autoLoginInProgress ||
    shouldStartLocalhostAutoLogin
  );
};

const LocalDevChrome = () => {
  const location = useLocation();

  if (!shouldShowLocalDevChrome(location.pathname)) {
    return null;
  }

  return (
    <>
      <DevLogDownloadButton />
    </>
  );
};

const GlobalEnvironmentSafetyChrome = () => {
  const location = useLocation();

  if (!shouldShowGlobalEnvironmentSafetyChrome(location.pathname)) {
    return null;
  }

  return <EnvironmentSafetyBadges />;
};

const createGlobalErrorHandlers = () => {
  const routeFromLocation = () =>
    sanitizeAdminRouteForLogging(window.location.pathname);

  const handleWindowError = (event: ErrorEvent) => {
    const unhandledError =
      event.error instanceof Error
        ? event.error
        : new Error(event.message || "Unhandled window error");
    FrontendLoggerFactory.getLogger()
      .withComponent("App")
      .withCorrelationId(generateCorrelationId())
      .error("UNHANDLED_ERROR: Global window onerror handler", unhandledError, {
        feature: "FND",
        operation: "UNHANDLED_ERROR",
        route: routeFromLocation(),
      });
  };

  const handleWindowRejection = (event: PromiseRejectionEvent) => {
    const reason = event.reason;
    const reasonError =
      reason instanceof Error
        ? reason
        : new Error(
            typeof reason === "string" ? reason : "Unhandled rejection",
          );

    FrontendLoggerFactory.getLogger()
      .withComponent("App")
      .withCorrelationId(generateCorrelationId())
      .error(
        "UNHANDLED_REJECTION: Global window promise rejection handler",
        reasonError,
        {
          feature: "FND",
          operation: "UNHANDLED_REJECTION",
          route: routeFromLocation(),
        },
      );
  };

  return {
    handleWindowError,
    handleWindowRejection,
  };
};

const AdminAppChrome = () => {
  const location = useLocation();
  const { user, loading } = useAuthState();
  const isAdminSessionStateIsolated = useAdminAuthSessionIsolation(
    user?.uid ?? null,
  );
  const adminE2eBypassEnabled = isAdminE2eBypassEnabled();
  const { claimCheckFailed, hasAdminClaim, isCheckingClaims } =
    useAdminClaimCheck({
      adminE2eBypassEnabled,
      claimCheckAttempt: 0,
      requiredClaims: ADMIN_REQUIRED_CLAIMS,
      user,
    });
  const { isLoggingOut, isRepairingLocalSession, logout } =
    useLocalAdminSessionRepair({
      claimCheckFailed,
      hasAdminClaim,
      isCheckingClaims,
      userId: user?.uid,
    });
  const appContent = (
    <>
      <GlobalEnvironmentSafetyChrome />
      <LocalDevChrome />
      <PreviewEmailOverrideGate />
      <App />
      <AdminAccessDeniedModal />
    </>
  );

  if (!isAdminSessionStateIsolated) {
    return (
      <AdminLoadingState
        subtitle={adminUiCopy.loading.signInStatus}
        title={adminUiCopy.loading.adminWorkspaceTitle}
      />
    );
  }

  if (isRepairingLocalSession) {
    return (
      <AdminLoadingState
        subtitle={adminUiCopy.loading.signInStatus}
        title={adminUiCopy.loading.adminWorkspaceTitle}
      />
    );
  }

  // Keep non-admin staff out of the admin shell entirely so they never reach
  // wildcard fallback routes or admin-only tools while claim resolution is in flight.
  if (
    shouldHoldForGlobalAdminClaimCheck({
      isAuthLoading: loading,
      isCheckingClaims,
      pathname: location.pathname,
      userPresent: Boolean(user),
    })
  ) {
    return (
      <AdminLoadingState
        subtitle={adminUiCopy.loading.signInStatus}
        title={adminUiCopy.loading.adminWorkspaceTitle}
      />
    );
  }

  if (
    user &&
    shouldRenderGlobalAdminAccessDenied({
      claimCheckFailed,
      hasAdminClaim,
      pathname: location.pathname,
      userPresent: true,
    })
  ) {
    return (
      <AdminAccessDeniedPanel
        isLoggingOut={isLoggingOut}
        onLogout={() => void logout()}
        user={user}
      />
    );
  }

  if (shouldBypassGlobalAdminHealthGate(location.pathname)) {
    return appContent;
  }

  return <ApiHealthGate>{appContent}</ApiHealthGate>;
};

function App() {
  // Use the proven working pattern - no AuthProvider needed!
  const { user, loading } = useAuthState();
  const location = useLocation();
  const sanitizedPathname = sanitizeAdminRouteForLogging(location.pathname);
  const [autoLoginInProgress, setAutoLoginInProgress] = useState(false);
  const [isFirebaseReady, setIsFirebaseReady] = useState(false);
  const [autoLoginAttempted, setAutoLoginAttempted] = useState(() =>
    wasLocalhostTokenHandoffLoaded(location.search),
  );
  const bootstrapLoggedRef = useRef(false);
  const sessionBootSignalLoggedRef = useRef(false);
  const firebaseBootstrapStartedAtRef = useRef<number | null>(null);
  const testUserId = resolveLocalhostAutoLoginUserId(
    import.meta.env.VITE_LOCALHOST_TEST_USER_FIREBASE_UUID,
  );
  const isLoginRoute = location.pathname === "/login";
  const loginSearchParams = new URLSearchParams(location.search);
  const protectedRouteReturnUrl = resolveProtectedRouteReturnUrl(
    location.search,
  );
  const autoLoginReturnUrl = resolveLocalhostAdminLoginReturnUrl({
    hash: location.hash,
    pathname: location.pathname,
    search: location.search,
  });
  const hasRedirectedFromProtectedRoute = Boolean(
    location.state?.from ?? protectedRouteReturnUrl,
  );
  const hasTokenLoginParam = loginSearchParams.has("token");
  const showFloatingTools = !isAdminScreenshotModeEnabled();
  const shouldStartLocalhostAutoLogin = shouldAttemptLocalhostAutoLogin({
    autoLoginAttempted,
    hasRedirectedFromProtectedRoute,
    hasTokenLoginParam,
    isAuthLoading: loading,
    isLoginRoute,
    localTestUserIdConfigured: testUserId.length > 0,
    userPresent: Boolean(user),
  });

  useEffect(() => {
    const { handleWindowError, handleWindowRejection } =
      createGlobalErrorHandlers();

    window.addEventListener("error", handleWindowError);
    window.addEventListener("unhandledrejection", handleWindowRejection);

    return () => {
      window.removeEventListener("error", handleWindowError);
      window.removeEventListener("unhandledrejection", handleWindowRejection);
    };
  }, []);

  // CRITICAL SLK-03-CROSS-TAB-NOTICE: listen from startup so a sign-out in
  // another tab is recorded before this tab is sent to sign-in.
  useEffect(() => installAdminCrossTabSignOutListener(window), []);

  useEffect(() => {
    initializePerformanceMonitoring();
  }, []);

  useEffect(() => {
    let isActive = true;
    if (!bootstrapLoggedRef.current) {
      bootstrapLoggedRef.current = true;
      logger.debug("Admin app bootstrap mounted", {
        operation: "admin_app_bootstrap_mount",
        route: sanitizedPathname,
        isLoginRoute,
        hasTokenLoginParam,
        hasRedirectedFromProtectedRoute,
        isFirebaseReady,
        loading,
        userPresent: Boolean(user),
      });
    }

    if (isFirebaseReady) return;

    // A route/auth rerender cleans up the previous waiter. Subscribe again to
    // the same initialization promise until it settles; a once-requested flag
    // would leave readiness false if that sole waiter's cleanup ran first.
    firebaseBootstrapStartedAtRef.current ??= Date.now();

    // Hold route decisions until Firebase finishes restoring persisted auth state.
    // Without this, a valid session can briefly look anonymous and bounce through `/login`.
    void waitForFirebaseInit().then(
      () => {
        if (!isActive) {
          return;
        }

        logger.debug("Firebase auth readiness resolved for admin bootstrap", {
          operation: "admin_app_bootstrap_firebase_ready",
          route: sanitizedPathname,
          durationMs:
            firebaseBootstrapStartedAtRef.current === null
              ? null
              : Date.now() - firebaseBootstrapStartedAtRef.current,
          isLoginRoute,
          hasTokenLoginParam,
          hasRedirectedFromProtectedRoute,
        });
        setIsFirebaseReady(true);
      },
      () => {
        if (!isActive) return;
        // Local initialization failures are terminal. Keep routes closed and
        // consume the rejection without exposing SDK errors or cloud fallback.
        logger.error(
          "Firebase auth readiness failed for admin bootstrap",
          undefined,
          {
            operation: "admin_app_bootstrap_firebase_failed",
            route: sanitizedPathname,
          },
        );
      },
    );

    return () => {
      isActive = false;
    };
  }, [
    hasRedirectedFromProtectedRoute,
    hasTokenLoginParam,
    isFirebaseReady,
    isLoginRoute,
    loading,
    sanitizedPathname,
    user,
  ]);

  useEffect(() => {
    if (!isFirebaseReady || sessionBootSignalLoggedRef.current) {
      return;
    }

    sessionBootSignalLoggedRef.current = true;
    emitAdminSessionBootSignal({
      authState: user ? "authenticated" : "anonymous",
      route: sanitizedPathname ?? "/",
    });
  }, [isFirebaseReady, sanitizedPathname, user]);

  // Auto-login for testing purposes
  useEffect(() => {
    if (!shouldStartLocalhostAutoLogin) {
      return;
    }

    setAutoLoginAttempted(true);
    const correlationId = generateCorrelationId();
    const requestLogger = logger.withCorrelationId(correlationId);

    const runAutoLogin = async () => {
      setAutoLoginInProgress(true);

      // Use the existing local Admin login endpoint so the one-time token carries
      // the Admin claim required by every protected route.
      requestLogger.debug("Auto-login requesting local Admin login", {
        operation: "auto_login_request_token",
        route: sanitizedPathname,
        hasRedirectedFromProtectedRoute,
        hasTokenLoginParam,
        isLoginRoute,
      });

      requestLogger.debug("Admin app holding render for localhost auto-login", {
        operation: "auto_login_hold_render",
        route: sanitizedPathname,
        shouldStartLocalhostAutoLogin,
      });

      try {
        const response = await fetch(
          buildLocalhostAdminLoginRequestUrl({
            apiBaseUrl: import.meta.env.VITE_API_URL,
            returnUrl: autoLoginReturnUrl,
            testUserId,
          }),
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              [CORRELATION_ID_HEADER]: correlationId,
            },
          },
        );

        if (!response.ok) {
          try {
            const errorData = (await response.json()) as {
              error?: string;
            };
            const errorMessage =
              typeof errorData.error === "string" &&
              errorData.error.trim().length > 0
                ? errorData.error
                : response.statusText;

            throw new Error(`HTTP ${response.status}: ${errorMessage}`);
          } catch {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }
        }

        const data = (await response.json()) as {
          loginUrl?: string;
        };
        if (!data.loginUrl) {
          throw new Error("No Admin login URL received in response");
        }

        requestLogger.debug("Auto-login handoff ready", {
          operation: "auto_login_success",
          route: sanitizedPathname,
          userIdPresent: true,
        });
        window.location.replace(data.loginUrl);
      } catch (error) {
        requestLogger.error(
          "Auto-login failed; falling back to manual login",
          error instanceof Error ? error : undefined,
          { operation: "auto_login_failed" },
        );
        setAutoLoginInProgress(false);
      }
    };

    void runAutoLogin();
  }, [
    autoLoginReturnUrl,
    hasRedirectedFromProtectedRoute,
    hasTokenLoginParam,
    isLoginRoute,
    sanitizedPathname,
    shouldStartLocalhostAutoLogin,
    testUserId,
  ]);

  // Public share links must not render the admin loading shell while auth is restoring.
  // That shell reads guarded admin identity and can redirect anonymous recipients.
  // @see docs/features/DOCS_SHARE_LINKS.md
  const isAuthStateResolving = shouldHoldForAppAuthResolution({
    autoLoginInProgress,
    isAuthLoading: loading === true,
    isFirebaseReady,
    pathname: location.pathname,
    shouldStartLocalhostAutoLogin,
  });

  useEffect(() => {
    logger.debug("Admin app auth resolution state updated", {
      operation: "admin_app_auth_resolution_state",
      route: sanitizedPathname,
      isAuthStateResolving,
      isFirebaseReady,
      loading,
      autoLoginInProgress,
      shouldStartLocalhostAutoLogin,
      userPresent: Boolean(user),
      isLoginRoute,
      hasTokenLoginParam,
      hasRedirectedFromProtectedRoute,
    });
  }, [
    autoLoginInProgress,
    hasRedirectedFromProtectedRoute,
    hasTokenLoginParam,
    isAuthStateResolving,
    isFirebaseReady,
    isLoginRoute,
    loading,
    sanitizedPathname,
    shouldStartLocalhostAutoLogin,
    user,
  ]);

  if (isAuthStateResolving) {
    return (
      <AdminLoadingState
        subtitle={adminUiCopy.loading.adminWorkspaceSubtitle}
        title={adminUiCopy.loading.adminWorkspaceTitle}
      />
    );
  }

  // Now safe to render - auth state is known
  return (
    <ErrorBoundary componentName="AppErrorBoundary">
      <Routes>
        <Route
          path="/login"
          element={
            <RoutePageLifecycle
              route={{ id: "login", path: "/login" }}
              fallback={<LoadingFallback />}
            >
              <Login />
            </RoutePageLifecycle>
          }
        />
        <Route
          path="/share/docs/:token"
          element={
            <RoutePageLifecycle
              route={{
                id: "shared_docs",
                path: "/share/docs/:token",
              }}
              fallback={<PublicSharedDocLoadingFallback />}
            >
              <SharedDocViewer />
            </RoutePageLifecycle>
          }
        />
        {ADMIN_ROUTES.map((route) => {
          const permissionRoute = (
            <PermissionProtectedRoute
              requiredPermission={route.requiredPermission}
              requiredPermissionFallbacks={route.requiredPermissionFallbacks}
              requiredMenuVisibility={route.requiredMenuVisibility}
              preserveOpenTemplateEditor={
                route.id === "v2_email_comms_templates"
              }
            >
              <RoutePageLifecycle route={route} fallback={<LoadingFallback />}>
                <route.Component />
              </RoutePageLifecycle>
            </PermissionProtectedRoute>
          );
          const claimRoute = (
            <GrowthFormClaimGate requiredClaims={ADMIN_REQUIRED_CLAIMS}>
              {permissionRoute}
            </GrowthFormClaimGate>
          );
          return (
            <Route
              key={route.id}
              path={route.path}
              element={
                <GrowthFormDraftBoundary
                  enabled={route.id === "v2_form_builder"}
                >
                  {route.id === "v2_crm_contact_record" ? (
                    <ContactNextActionRouteCustody>
                      {claimRoute}
                    </ContactNextActionRouteCustody>
                  ) : (
                    claimRoute
                  )}
                </GrowthFormDraftBoundary>
              }
            />
          );
        })}
        <Route
          path="*"
          element={
            <RoutePageLifecycle
              route={{
                id: "route_unavailable",
                path: "*",
              }}
              fallback={<LoadingFallback />}
            >
              <RouteUnavailablePage />
            </RoutePageLifecycle>
          }
        />
      </Routes>
      {/* Log issue and AI Assistant now open as side panels from the shell's
          top bar (Nav PRD §7, §8, §11); only the static export launcher
          remains outside the shell. */}
      {showFloatingTools ? (
        <AdminStaticExportLauncherMount currentUserId={user?.uid ?? null} />
      ) : null}
    </ErrorBoundary>
  );
}

export const ColorModeContext = createContext({
  toggleColorMode: () => {},
  mode: "" as "light" | "dark",
});

/**
 * Determines the initial admin theme mode.
 *
 * @remarks
 * Forced to light mode — the admin site is not yet ready for dark mode.
 */
const getInitialMode = (): "light" | "dark" => "light";

/**
 * Top-level app shell for HellocashflowAdmin.
 *
 * @remarks
 * Owns theme selection/persistence and renders the route tree via `App()`.
 */
export default function AdminApp() {
  const [mode, setMode] = useState<"light" | "dark">(getInitialMode());

  const toggleColorMode = useMemo(
    () => () => {
      setMode((prevMode) => (prevMode === "light" ? "dark" : "light"));
    },
    [],
  );

  useEffect(() => {
    localStorage.setItem("admin-theme-mode", mode);
    if (typeof document !== "undefined") {
      document.documentElement.setAttribute("data-admin-color-scheme", mode);
    }
  }, [mode]);

  const theme = useMemo(() => createAdminTheme(mode), [mode]);

  // No AuthProvider needed! Firebase is initialized early in index.tsx
  return (
    <ColorModeContext.Provider value={{ toggleColorMode, mode }}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AdminAppChrome />
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}
